import React, { useEffect, useMemo, useState } from 'react';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { GoalsIcon, RelationshipsIcon } from '../global/CustomIcons';
import BoltIcon from '@mui/icons-material/Bolt';

import { fetchRequest as goalsFetch } from '../../redux/actions/discovery/goals';
import { fetchRequest as deleteGoalRequest } from '../../redux/actions/discovery/deleteGoal';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import StatusChip from '../common/StatusChip';
import RowActions from '../common/RowActions';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';

const GOAL_TYPE_LABELS = {
  forecast_value: 'Forecast value',
  forecast_distribution: 'Forecast distribution',
  trend: 'Trend',
  anomaly: 'Anomaly detection',
  rank_by_risk: 'Fleet · rank by risk',
  rank_by_growth: 'Fleet · rank by growth',
};

// Fleet goals target a population, not one signal — show the selector.
const goalSublabel = (row) => {
  if (row.goal_kind === 'fleet') {
    const sel = row.target_selector || {};
    const members = row.fleet?.member_count;
    const base = sel.type === 'group' ? `group: ${sel.value}` : sel.value;
    return members ? `${base} · ${Number(members).toLocaleString()} members` : base;
  }
  return row.target_key;
};

// Goal lifecycle/serving status -> chip tone. ``serving`` = a deployed
// champion is producing forecasts; ``live`` is a legacy alias (pre-rename)
// kept so older goals still render green. This is separate from the goal's
// *data mode* (live vs historical), shown by ``dataModeChip`` below.
const STATUS_TONES = {
  idle: 'neutral',
  running: 'info',
  serving: 'success',
  live: 'success',
  failed: 'error',
};

// Is this goal serving forecasts? (covers the legacy ``live`` value too)
const isServingStatus = (s) => s === 'serving' || s === 'live';

// Data dimension, orthogonal to status: does the goal's data stream in
// (``options.live``, the default) or is it a fixed historical window?
const dataModeChip = (goal) => {
  const isLiveData = (goal?.options?.live) !== false;
  return (
    <StatusChip
      status=""
      tone={isLiveData ? 'info' : 'neutral'}
      label={isLiveData ? 'Live data' : 'Historical'}
      icon={null}
      tooltip={isLiveData
        ? 'Data streams in continuously — live monitoring and auto-retrain are enabled'
        : 'Fixed historical window — no new data arrives'}
    />
  );
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch (_e) { return '—'; }
};

const safeRelative = (d) => {
  try { if (!d) return ''; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return ''; }
};

const horizonLabel = (goal) => {
  const bins = goal?.horizon_bins;
  const secs = goal?.interval_seconds;
  if (!bins || !secs) return '—';
  const total = bins * secs;
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return `${hours}h`;
  return `${Math.floor(total / 60)}m`;
};

const DiscoveryPage = ({ goals, isLoading, fetchGoals, deleteGoal, deleteState }) => {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const rows = useMemo(() => goals || [], [goals]);

  // Live (no polling): refetch the listing when a goal's run/tournament/status
  // changes (these fan out to the workspace channel this page is subscribed
  // to). Reconnect (msg=null) reconciles too.
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string' && (
      msg.event === 'goal.created'
      || msg.event === 'goal.deleted'
      || msg.event.indexOf('goal.run.') === 0
      || msg.event.indexOf('goal.status') === 0
      || msg.event.indexOf('goal.tournament.') === 0
      || msg.event.indexOf('goal.fleet.') === 0
    ),
    () => fetchGoals(),
  );

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter !== 'all') {
      out = out.filter((r) => (statusFilter === 'serving'
        ? isServingStatus(r.status)
        : r.status === statusFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        (r.name || '').toLowerCase().includes(q)
        || (r.target_key || '').toLowerCase().includes(q)
        || ((r.target_selector || {}).value || '').toLowerCase().includes(q));
    }
    return out;
  }, [rows, statusFilter, search]);

  const sorted = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av; let bv;
      switch (sortColumn) {
        case 'name': av = a.name || ''; bv = b.name || ''; break;
        case 'edges_count': av = a.edges_count || 0; bv = b.edges_count || 0; break;
        case 'status': av = a.status || ''; bv = b.status || ''; break;
        default: av = a.created_at || ''; bv = b.created_at || '';
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortColumn, sortDirection]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (search.trim() ? 1 : 0);
  const clearAll = () => { setStatusFilter('all'); setSearch(''); setPage(0); };

  const columns = useMemo(() => ([
    {
      key: 'name',
      label: 'Goal',
      sortable: true,
      render: (row) => (
        <EntityCell
          id={row.goal_id}
          tone={STATUS_TONES[row.status] || 'neutral'}
          label={row.name}
          sublabel={goalSublabel(row)}
        />
      ),
    },
    {
      key: 'goal_type',
      label: 'Type',
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {GOAL_TYPE_LABELS[row.goal_type] || row.goal_type}
        </Typography>
      ),
    },
    {
      key: 'horizon',
      label: 'Horizon',
      render: (row) => (
        <Typography variant="body2" color="text.secondary">{horizonLabel(row)}</Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <StatusChip status={row.status} tone={STATUS_TONES[row.status] || 'neutral'} />
          {dataModeChip(row)}
          {row.shock_state === 'shock' && (
            <Tooltip title="Shock mode — an out-of-distribution event is active" arrow>
              <BoltIcon color="warning" sx={{ fontSize: 16 }} />
            </Tooltip>
          )}
          {row.shock_state === 'recovery' && (
            <Tooltip title="Recovering — the shock is fading; serving anneals back to normal" arrow>
              <BoltIcon color="disabled" sx={{ fontSize: 16 }} />
            </Tooltip>
          )}
          {row?.last_run?.status === 'failed' && row.status !== 'failed' && (
            <Tooltip title={row.last_run.error || 'Last run failed'} arrow>
              <Typography variant="caption" color="error.main">last run failed</Typography>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      key: 'edges_count',
      label: 'Drivers',
      sortable: true,
      align: 'right',
      render: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <RelationshipsIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="body2">{row.edges_count ?? 0}</Typography>
        </Stack>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row) => (
        <Tooltip title={safeRelative(row.created_at)} arrow placement="top">
          <Typography variant="body2" color="text.secondary">
            {formatDate(row.created_at)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      stopRowClick: true,
      render: (row) => (
        <RowActions
          onOpen={() => navigate(`/goals/${row.goal_id}`)}
          items={[
            {
              label: 'Delete',
              icon: DeleteOutlineIcon,
              danger: true,
              onClick: () => setDeleteTarget(row),
            },
          ]}
        />
      ),
    },
  ]), [navigate]);

  const filtersNode = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search goals…"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
        sx={{ minWidth: 220 }}
      />
      <TextField
        select
        size="small"
        label="Status"
        value={statusFilter}
        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="all">All</MenuItem>
        <MenuItem value="serving">Serving</MenuItem>
        <MenuItem value="running">Running</MenuItem>
        <MenuItem value="idle">Idle</MenuItem>
        <MenuItem value="failed">Failed</MenuItem>
      </TextField>
    </Stack>
  );

  const actionsNode = (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Watch a whole population of signals and rank which members matter — a live leaderboard" arrow>
        <CMButtonOutlined startIcon={<LeaderboardIcon />} onClick={() => navigate('/goals/create?kind=fleet')} size="small">
          New Fleet Goal
        </CMButtonOutlined>
      </Tooltip>
      <CMButton startIcon={<AddIcon />} onClick={() => navigate('/goals/create')} size="small">
        New Goal
      </CMButton>
    </Stack>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Goals', icon: <GoalsIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Pick a target signal and a goal — discovery finds which signals and events
        actually drive it, with lags, direction, and validated out-of-sample lift.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${sorted.length} of ${rows.length}` : null}
      />

      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(row) => row.goal_id}
        onRowClick={(row) => navigate(`/goals/${row.goal_id}`)}
        loading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={sorted.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        emptyState={{
          icon: <GoalsIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No goals match your filters' : 'No goals yet',
          message: activeFilterCount > 0
            ? 'Try adjusting or clearing your filters.'
            : 'Create a goal to find out what drives one of your signals.',
          action: activeFilterCount === 0 ? (
            <CMButton startIcon={<AddIcon />} onClick={() => navigate('/goals/create')} size="small">
              New Goal
            </CMButton>
          ) : null,
        }}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete goal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{deleteTarget?.name}” and its discovered relationships will be archived.
            Live monitoring for this goal stops. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={deleteState?.isLoading}
            onClick={() => {
              deleteGoal({ goalId: deleteTarget.goal_id });
              setDeleteTarget(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  goals: state.discovery_goals?.response || [],
  isLoading: Boolean(state.discovery_goals?.isLoading),
  deleteState: state.discovery_delete_goal,
});

const mapDispatchToProps = (dispatch) => ({
  fetchGoals: () => dispatch(goalsFetch()),
  deleteGoal: (payload) => dispatch(deleteGoalRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryPage);
